import { useEffect, useState } from 'react';
import { useStoreState } from '~/store';
import {
    getPeriodicCheckQueueVersion,
    listQueuedPeriodicChecks,
    subscribePeriodicCheckQueue,
} from '~/data/sync/periodicCheckQueue';

export type PendingPeriodicCheckState = 'draft' | 'submitted';

export const pendingPeriodicCheckKey = (id: string | number, inspectionType: number) =>
    `${id}:${Number(inspectionType)}`;

export function isPeriodicCheckAwaitingSync(
    pending: Record<string, PendingPeriodicCheckState>,
    loaded: boolean,
    machineId: string | number,
    inspectionType: number,
    pendingSync?: boolean,
): boolean {
    const queued = pending[pendingPeriodicCheckKey(machineId, inspectionType)] === 'submitted';
    if (loaded) {
        return queued;
    }
    return queued || !!pendingSync;
}

/** One queue scan for the open machine and its linked equipment. */
export function usePendingPeriodicChecks(
    targets: { id: string | number; inspectionType: number }[],
    refreshKey = '',
): { pending: Record<string, PendingPeriodicCheckState>, loaded: boolean } {
    const userId = useStoreState(state => state.user.data?.id);
    const [queueVersion, setQueueVersion] = useState(getPeriodicCheckQueueVersion);
    const [pending, setPending] = useState<Record<string, PendingPeriodicCheckState>>({});
    const [loaded, setLoaded] = useState(false);
    const targetKey = targets.map(target => pendingPeriodicCheckKey(target.id, target.inspectionType)).join('|');

    useEffect(() => subscribePeriodicCheckQueue(setQueueVersion), []);

    useEffect(() => {
        let cancelled = false;
        if (!userId || !window.sqlitePlugin) {
            setPending({});
            setLoaded(true);
            return;
        }
        listQueuedPeriodicChecks(Number(userId)).then(rows => {
            if (cancelled) {
                return;
            }
            const next: Record<string, PendingPeriodicCheckState> = {};
            rows.forEach(row => {
                next[pendingPeriodicCheckKey(row.machineId, Number(row.inspectionType))] = row.isDraft
                    ? 'draft'
                    : 'submitted';
            });
            setPending(next);
            setLoaded(true);
        }).catch(() => {
            if (!cancelled) {
                setPending({});
                setLoaded(true);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [userId, targetKey, refreshKey, queueVersion]);

    return { pending, loaded };
}
